import {
  GatewayCloseCode,
  GatewayOp,
  type GatewayEventName,
  type GatewayEvents,
  type Message,
  type PresenceStatus,
} from '@comms/shared';
import {
  applyDispatch,
  displayName,
  mentionsMe,
  notifyLevel,
  useStore,
  wantsNotification,
} from '../store';
import { messageSound } from './sounds';
import { checkForUpdate } from './update';
import { getEngine, syncDm } from './e2ee';
import { joinVoice } from './voice';

type Handler = (t: GatewayEventName, d: unknown) => void;

/** Browser gateway client: identify via cookie, heartbeats, resume with backoff. */
export class GatewayClient {
  private ws: WebSocket | null = null;
  private seq = 0;
  private sessionId: string | null = null;
  private heartbeat: number | null = null;
  private acked = true;
  private attempts = 0;
  private stopped = false;
  private readonly handlers = new Set<Handler>();

  constructor(
    private readonly onAuthLost: () => void,
    /**
     * Asks the server whether we're still signed in after the gateway turned us away:
     * true = signed in, false = signed out, null = couldn't tell (offline).
     */
    private readonly stillSignedIn: () => Promise<boolean | null> = async () => false,
  ) {}

  on(h: Handler) {
    this.handlers.add(h);
    return () => this.handlers.delete(h);
  }

  connect() {
    this.stopped = false;
    useStore.setState({ connection: this.sessionId ? 'reconnecting' : 'connecting' });
    const url = `${location.protocol === 'https:' ? 'wss' : 'ws'}://${location.host}/gateway`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onmessage = (ev) =>
      this.handle(
        JSON.parse(ev.data as string) as {
          op: number;
          t?: GatewayEventName;
          s?: number;
          d: unknown;
        },
      );
    ws.onclose = (ev) => {
      if (this.ws === ws) this.ws = null;
      this.closed(ev.code);
    };
  }

  disconnect() {
    this.stopped = true;
    this.stopHeartbeat();
    this.ws?.close(1000);
    this.ws = null;
    this.sessionId = null;
  }

  private background = false;
  private retry: number | undefined;

  /**
   * The app went to (or came back from) the background. It stays connected, so the user stays
   * online, but the server sends this device push notifications meanwhile. Back in the
   * foreground, reconnect right away if the connection dropped in the background.
   */
  setBackground(background: boolean) {
    this.background = background;
    this.send({ op: GatewayOp.APP_STATE, d: { background } });
    if (background || this.stopped || this.ws) return;
    window.clearTimeout(this.retry);
    this.attempts = 0;
    this.connect();
  }

  setPresence(status: PresenceStatus, customStatus: string | null = null) {
    this.send({ op: GatewayOp.PRESENCE_UPDATE, d: { status, customStatus } });
  }

  private send(msg: unknown) {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  private handle(msg: { op: number; t?: GatewayEventName; s?: number; d: unknown }) {
    switch (msg.op) {
      case GatewayOp.HELLO: {
        const { heartbeatInterval } = msg.d as { heartbeatInterval: number };
        this.startHeartbeat(heartbeatInterval);
        if (this.sessionId)
          this.send({
            op: GatewayOp.RESUME,
            d: { sessionId: this.sessionId, seq: this.seq, background: this.background },
          });
        else this.send({ op: GatewayOp.IDENTIFY, d: { background: this.background } });
        return;
      }
      case GatewayOp.HEARTBEAT_ACK:
        this.acked = true;
        return;
      case GatewayOp.INVALID_SESSION:
        this.sessionId = null;
        this.seq = 0;
        return;
      case GatewayOp.DISPATCH:
        if (msg.s) this.seq = msg.s;
        this.dispatch(msg.t!, msg.d);
        return;
    }
  }

  private dispatch(t: GatewayEventName, d: unknown) {
    if (t === 'READY') {
      this.sessionId = (d as GatewayEvents['READY']).sessionId;
      // A fresh connection (e.g. after a server deploy): make sure we run the latest build.
      void checkForUpdate();
      this.attempts = 0;
    }
    if (t === 'RESUMED') {
      this.attempts = 0;
      useStore.setState({ connection: 'ready' });
    }
    applyDispatch(t, d as never);
    this.sideEffects(t, d);
    for (const h of this.handlers) h(t, d);
  }

  private sideEffects(t: GatewayEventName, d: unknown) {
    const engine = getEngine();
    switch (t) {
      case 'E2EE_MESSAGE_CREATE': {
        const m = d as GatewayEvents['E2EE_MESSAGE_CREATE'];
        syncDm(m.channelId);
        const level = notifyLevel(useStore.getState(), m.channelId);
        if (
          m.kind === 'application' &&
          m.senderUserId !== useStore.getState().me?.id &&
          level !== 'muted' &&
          level !== 'nothing'
        ) {
          notify(
            `${displayName(useStore.getState(), m.senderUserId)}`,
            'New encrypted message',
            m.channelId,
          );
          messageSound({ channelId: m.channelId, authorId: m.senderUserId, mentioned: false });
        }
        break;
      }
      case 'E2EE_KEYS_REQUESTED': {
        const r = d as GatewayEvents['E2EE_KEYS_REQUESTED'];
        void getEngine()
          ?.onKeysRequested(r.channelId, r.deviceId)
          .catch((err: unknown) => console.warn('e2ee sync failed', r.channelId, err));
        break;
      }
      case 'E2EE_GROUP_RESET':
        syncDm((d as GatewayEvents['E2EE_GROUP_RESET']).channelId);
        break;
      case 'MLS_WELCOME':
        if (engine && (d as GatewayEvents['MLS_WELCOME']) && engine.deviceId) {
          void engine
            .processWelcomes()
            .catch((err: unknown) => console.warn('welcome failed', err));
        }
        break;
      case 'KEY_PACKAGES_LOW':
        if (engine && (d as GatewayEvents['KEY_PACKAGES_LOW']).deviceId === engine.deviceId)
          void engine.refillKeyPackages(true);
        break;
      case 'DEVICE_LIST_UPDATE': {
        // A participant gained or lost a device: re-check membership of our shared DMs.
        const { userId } = d as GatewayEvents['DEVICE_LIST_UPDATE'];
        for (const c of Object.values(useStore.getState().channels)) {
          if (c.guildId === null && c.recipients?.some((r) => r.id === userId)) syncDm(c.id);
        }
        break;
      }
      case 'CHANNEL_CREATE':
      case 'CHANNEL_UPDATE': {
        const c = d as GatewayEvents['CHANNEL_CREATE'];
        if (c.guildId === null) syncDm(c.id);
        break;
      }
      case 'MESSAGE_DELETE': {
        const m = d as GatewayEvents['MESSAGE_DELETE'];
        if (m.guildId === null && engine) void engine.forgetServerMessage(m.channelId, m.id);
        break;
      }
      case 'MESSAGE_CREATE': {
        const m = d as GatewayEvents['MESSAGE_CREATE'];
        const s = useStore.getState();
        messageSound({ channelId: m.channelId, authorId: m.author.id, mentioned: mentionsMe(m) });
        if (m.author.id !== s.me?.id && shouldNotify(m)) {
          notify(
            displayName(s, m.author.id, m.guildId),
            m.content.slice(0, 200),
            m.channelId,
            m.guildId,
          );
        }
        break;
      }
      case 'VOICE_MOVE': {
        const { guildId, channelId } = d as GatewayEvents['VOICE_MOVE'];
        void joinVoice(guildId, channelId);
        break;
      }
      case 'SESSION_REVOKED':
        this.onAuthLost();
        break;
    }
  }

  private closed(code: number) {
    this.stopHeartbeat();
    if (this.stopped) return;
    if (
      code === GatewayCloseCode.AUTHENTICATION_FAILED ||
      code === GatewayCloseCode.NOT_AUTHENTICATED
    ) {
      // Only log out if the server confirms the session is gone (expired, signed out,
      // revoked); a rejected resume or a slow identify just means connecting afresh.
      this.sessionId = null;
      useStore.setState({ connection: 'reconnecting' });
      void this.stillSignedIn().then((signedIn) => {
        if (this.stopped) return;
        if (signedIn === false) this.onAuthLost();
        else this.reconnectLater();
      });
      return;
    }
    if (code === GatewayCloseCode.INVALID_SEQ || code === GatewayCloseCode.SESSION_TIMED_OUT)
      this.sessionId = null;
    this.reconnectLater();
  }

  private reconnectLater() {
    useStore.setState({ connection: 'reconnecting' });
    const delay = Math.min(30_000, 1000 * 2 ** this.attempts++) * (0.5 + Math.random() / 2);
    this.retry = window.setTimeout(() => !this.stopped && this.connect(), delay);
  }

  private startHeartbeat(interval: number) {
    this.stopHeartbeat();
    this.acked = true;
    const beat = () => {
      if (!this.acked) {
        this.ws?.close(4000, 'heartbeat not acked');
        return;
      }
      this.acked = false;
      this.send({ op: GatewayOp.HEARTBEAT, d: this.seq });
    };
    window.setTimeout(beat, interval * Math.random());
    this.heartbeat = window.setInterval(beat, interval);
  }

  private stopHeartbeat() {
    if (this.heartbeat) window.clearInterval(this.heartbeat);
    this.heartbeat = null;
  }
}

/** Same rules as push: the channel's level (thread → channel → server), threads only if joined. */
function shouldNotify(m: Message) {
  return wantsNotification(useStore.getState(), m.channelId, mentionsMe(m));
}

function notify(title: string, body: string, channelId: string, guildId: string | null = null) {
  if (
    !document.hidden ||
    typeof Notification === 'undefined' ||
    Notification.permission !== 'granted'
  )
    return;
  const n = new Notification(title, { body, tag: channelId, icon: '/favicon.svg' });
  n.onclick = () => {
    window.focus();
    window.location.hash = '';
    window.history.pushState({}, '', `/channels/${guildId ?? '@me'}/${channelId}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
}
